import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DealOriginApiService } from '../services/deal-origin-api.service';
import {
  DealOriginRequest,
  DealOriginResponse,
} from '../models/deal-origin.model';
import { PreventSpecialCharactersDirective } from '../../../shared/directives/prevent-special-characters.directive';
import { PreventLongNamesDirective } from '../../../shared/directives/prevent-long-names.directive';
import { PreventLongDescriptionsDirective } from '../../../shared/directives/prevent-long-descriptions.directive';
import { ValidatorFormsInformation } from '../../../shared/utils/validator-forms-information.utils';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-deal-origin-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    PreventSpecialCharactersDirective,
    PreventLongNamesDirective,
    PreventLongDescriptionsDirective,
  ],
  templateUrl: './deal-origin-form.component.html',
})
export class DealOriginFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;
  dealOriginsExisting: DealOriginResponse[] = [];

  // Form fields
  name = '';
  private originalName = '';
  code = '';
  description = '';

  constructor(
    private api: DealOriginApiService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
    this.getDealOriginsExisting();
  }

  setError(message: string) {
    this.error = message;
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    this.api.getById(id).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.code = item.code;
        this.description = item.description || '';
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el origen';
        this.loading = false;
      },
    });
  }

  getDealOriginsExisting() {
    this.api.getAll().subscribe({
      next: (dealOrigins) => {
        this.dealOriginsExisting = dealOrigins ?? [];
      },
      error: (err) => {
        this.toast.error({
          title: 'Origenes',
          message:
            err.error?.message || 'No se pudo obtener origenes existentes',
        });
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing
      ? 'Editar Origen de Negocio'
      : 'Nuevo Origen de Negocio';
  }

  guardar(): void {
    const nameValidation = ValidatorFormsInformation.validateText(
      this.name,
      'nombre',
      30,
    );
    if (nameValidation) {
      this.error = nameValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const codeValidation = ValidatorFormsInformation.validateText(
      this.code,
      'código',
      30,
    );

    if (codeValidation) {
      this.error = codeValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const descriptionValidation = ValidatorFormsInformation.validateText(
      this.description,
      'descripción',
      500,
    );

    if (descriptionValidation) {
      this.error = descriptionValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    if (
      this.dealOriginsExisting.some(
        (dealOrigin) =>
          dealOrigin.name.toLowerCase() === this.name.toLowerCase(),
      )
    ) {
      this.error = 'El nombre ya se encuentra en uso';
      this.toast.warning({ title: 'Nombre duplicado', message: this.error });
      return;
    }
    if (
      this.dealOriginsExisting.some(
        (dealOrigin) =>
          dealOrigin.code.toUpperCase() ===
          this.code.toUpperCase().replaceAll(' ', '_'),
      )
    ) {
      this.error = 'El código ya se encuentra en uso';
      this.toast.warning({ title: 'Código duplicado', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';
    const request: DealOriginRequest = {
      name: this.name.trim(),
      code: this.code.trim().toUpperCase().replaceAll(' ', '_'),
      description: this.description.trim() || undefined,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/origenes']);
          this.toast.updated('Origen',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el origen';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/origenes']);
          this.toast.created('Origen', `Se creó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el origen';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-deals/origenes']);
  }
}
